import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/AppNavbar.css';

export const AppNavbar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { 
    getAppName, 
    getLogo, 
    getPrimaryColor, 
    hasFeature 
  } = useApp();
  
  const { currentUser, logout, hasRole } = useAuth();

  // Force the navbar to show regardless of feature flag
  // const showNavbar = hasFeature('navbar');
  const showNavbar = true;
  
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close sidebar when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Always show the navbar
  // if (!showNavbar) {
  //   return null;
  // }

  return (
    <>
      {/* Main Navbar */}
      <nav className={`app-navbar ${scrolled ? 'scrolled' : ''}`} style={{ backgroundColor: getPrimaryColor() }}>
        <div className="navbar-container">
          <div className="navbar-brand">
            <Link to="/" className="logo-link">
              {console.log('Rendering logo with URL:', getLogo())}
              <img 
                src={getLogo()} 
                alt={`${getAppName()} logo`} 
                className="navbar-logo"
                onError={(e) => {
                  console.error('Logo failed to load:', e);
                  e.target.src = 'default-logo.png';
                  e.target.onerror = null; // Prevent infinite error loop
                }}
              />
              <h1 className="navbar-title">{getAppName()}</h1>
            </Link>
          </div>

          <button 
            className="menu-toggle"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <span className="hamburger"></span>
          </button>
        </div>
      </nav>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">Menu</h2>
          <button 
            className="close-sidebar"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            &times;
          </button>
        </div>

        <div className="sidebar-content">
          {currentUser ? (
            <div className="user-section">
              <div className="user-profile">
                <div className="avatar">
                  {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : 
                   currentUser.email ? currentUser.email.charAt(0).toUpperCase() : '?'}
                </div>
                <div className="user-info">
                  <p className="user-name">{currentUser.name || currentUser.email}</p>
                  <p className="user-role">{isAppEditor ? 'Editor' : 'User'}</p>
                </div>
              </div>
            </div>
          ) : null}

          <nav className="sidebar-nav">
            <Link 
              to="/dashboard" 
              className={`nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
            >
              <i className="fas fa-home"></i>
              <span>Dashboard</span>
            </Link>
            
            <Link 
              to="/courses" 
              className={`nav-item ${location.pathname === '/courses' || location.pathname.startsWith('/courses/') ? 'active' : ''}`}
            >
              <i className="fas fa-book"></i>
              <span>Courses</span>
            </Link>
            
            {hasFeature('bible') && (
              <Link 
                to="/bible" 
                className={`nav-item ${location.pathname.startsWith('/bible') ? 'active' : ''}`}
              >
                <i className="fas fa-bible"></i>
                <span>Bible</span>
              </Link>
            )}
            
            {hasFeature('family') && (
              <Link 
                to="/family" 
                className={`nav-item ${location.pathname.startsWith('/family') || location.pathname.startsWith('/reading-reports') || location.pathname.startsWith('/reader/') ? 'active' : ''}`}
              >
                <i className="fas fa-users"></i>
                <span>Family</span>
              </Link>
            )}
            
            {hasFeature('chat') && (
              <Link 
                to="/chat" 
                className={`nav-item ${location.pathname.startsWith('/chat') ? 'active' : ''}`}
              >
                <i className="fas fa-comment-alt"></i>
                <span>Chat</span>
              </Link>
            )}
            
            {/* Editor-specific links */}
            {isAppEditor && (
              <div className="editor-section">
                <h3 className="section-title">Editor Tools</h3>
                <Link 
                  to={`/apps/1/courses`} 
                  className={`nav-item ${location.pathname.startsWith('/apps') ? 'active' : ''}`}
                >
                  <i className="fas fa-edit"></i>
                  <span>Manage Courses</span>
                </Link>
              </div>
            )}
            
            {/* Admin links */}
            {hasRole('superadmin') && (
              <div className="admin-section">
                <h3 className="section-title">Admin</h3>
                <Link 
                  to="/admin" 
                  className={`nav-item ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
                >
                  <i className="fas fa-shield-alt"></i>
                  <span>Admin Dashboard</span>
                </Link>
              </div>
            )}
          </nav>
          
          {/* Bottom actions */}
          <div className="sidebar-footer">
            {currentUser ? (
              <>
                <Link 
                  to="/profile" 
                  className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
                >
                  <i className="fas fa-user-cog"></i>
                  <span>Profile</span>
                </Link>
                <button onClick={handleLogout} className="nav-item logout-button">
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <Link 
                to="/auth" 
                className={`nav-item ${location.pathname === '/auth' ? 'active' : ''}`}
              >
                <i className="fas fa-sign-in-alt"></i>
                <span>Login</span>
              </Link>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}; 